import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button, Select } from '../../../custom';
import Input from '../../../custom/input/input';
import { createUpdatePermission } from '../request';
import { StatusOptions } from '../../../requests';

interface Props {
  permission: Permission;
  handleClose: () => void;
  refetch: () => void;
}

const EditPermission = ({ permission, handleClose, refetch }: Props) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const validate = Yup.object().shape({
    name: Yup.string().required('Permission name is required'),
    status: Yup.string().required('Status is required'),
  });

  const updatePermissionMutation = useMutation({
    mutationFn: createUpdatePermission,
  });

  const updatePermissionHandler = async (values: FormikValues) => {
    const payload: UpdatePermissionPayload = {
      id: permission.id,
      name: values.name,
      isActive: values.status === 'true',
    };

    try {
      await updatePermissionMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: 'Success',
            description: data?.message || 'Permission updated successfully',
          });

          queryClient.refetchQueries({ queryKey: ['get-all-permissions'] });
          refetch();

          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to update permission',
      });
    }
  };

  return (
    <Formik
      initialValues={{
        name: permission.name,
        status: String(permission.isActive),
      }}
      onSubmit={(values) => {
        updatePermissionHandler(values);
      }}
      validationSchema={validate}
      enableReinitialize={true}
    >
      {() => {
        return (
          <Form className="fields">
            <Input label="Permission Name" placeholder="Input Permission Name" name="name" />

            <Select
              name="status"
              placeholder="Select Status"
              label="Status"
              options={
                <>
                  {StatusOptions.map((option: any) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </>
              }
            />

            <div className="btn-group">
              <Button onClick={handleClose} variant="text" text="Cancel" />
              <Button
                text={updatePermissionMutation.isPending ? 'Updating...' : 'Update'}
                type="submit"
                isLoading={updatePermissionMutation?.isPending}
                disabled={updatePermissionMutation?.isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditPermission;
